import InputGroup from "@/components/InputGroup";
import Select from "@/components/Select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useTranslation } from "react-i18next";
import { Controller, useFormContext } from "react-hook-form";
import { RegisterCompany } from "@/schema/RegisterCompany";
import { useId, useState } from "react";
import TermsOfServiceDialog from "@/src/register-company/_components/TermsOfServiceDialog";

interface RegisterCompanyFormProps {
  turnstileRef: (node: HTMLDivElement | null) => void;
  turnstileError: string | null;
}

function RegisterCompanyForm({
  turnstileRef,
  turnstileError,
}: RegisterCompanyFormProps) {
  const { t } = useTranslation();
  const id = useId();
  const hearingScheduleId = useId();
  const [isTermsOpen, setIsTermsOpen] = useState(false);
  const {
    register,
    control,
    setValue,
    formState: { errors },
  } = useFormContext<RegisterCompany>();

  return (
    <div className="mx-auto flex w-full max-w-[640px] flex-col gap-6 px-6 pb-8 pt-4">
      <div className="rounded-xl border border-cool-gray-200 bg-white px-6 py-6 shadow-sm">
        {/* <div className="text-lg font-semibold text-cool-gray-900">
          {t("company-user-info")}
        </div> */}
        {/* <p className="mt-1 text-sm text-cool-gray-600">
          {t("company-user-information")}
        </p> */}
        <div className="grid w-full grid-cols-2 gap-6">
          <InputGroup
            required
            type="email"
            label={t("email")}
            error={errors.user_email?.message}
            className="col-span-full"
            register={register("user_email")}
          />
          <div className="col-span-full">
            <div className="mb-1">{t("plan-selection")}</div>
            <Controller
              control={control}
              name="plan_selection"
              render={({ field }) => (
                <Select
                  className="w-full rounded-md border"
                  value={field.value}
                  onChange={field.onChange}
                  options={[
                    {
                      value: "assessment-with-consult",
                      label: t("plan-selection-with-consult"),
                    },
                    {
                      value: "assessment-only",
                      label: t("plan-selection-assessment-only"),
                    },
                  ]}
                />
              )}
            />
            {errors.plan_selection && (
              <p className="text-red-500">
                {t("plan-selection-required")}
              </p>
            )}
          </div>
          <InputGroup
            required
            type="password"
            label={t("password")}
            className="col-span-full"
            error={errors.user_password?.message}
            register={register("user_password")}
          />
          <InputGroup
            required
            type="password"
            label={t("confirm-password")}
            className="col-span-full"
            error={errors.user_password_confirmation?.message}
            register={register("user_password_confirmation")}
          />
        </div>
      </div>
      <div className="grid w-full grid-cols-2 gap-6">
        <div className="col-span-full flex flex-col gap-4 rounded-xl border border-cool-gray-200 bg-white px-6 py-6 shadow-sm">
          <div className="flex flex-col gap-2">
            <Label
              label={t("hearing-schedule-preference-label")}
              htmlFor={hearingScheduleId}
              required
            />
            <Textarea
              id={hearingScheduleId}
              rows={4}
              placeholder={t("hearing-schedule-preference-placeholder")}
              required
              {...register("hearing_schedule_preference")}
            />
            {errors.hearing_schedule_preference?.message && (
              <p className="text-sm text-red-500">
                {errors.hearing_schedule_preference?.message}
              </p>
            )}
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
            {t("hearing-schedule-preference-info")}
          </div>
        </div>
        <div className="col-span-full flex flex-col gap-2">
          <Controller
            control={control}
            name="agree_to_terms"
            render={({ field }) => (
              <label
                htmlFor={id}
                className="rounded-lg border border-cool-gray-200 bg-white px-5 py-4 shadow-sm"
              >
                <div className="flex items-start gap-3">
                  <Checkbox
                    id={id}
                    checked={field.value}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setIsTermsOpen(true);
                        return;
                      }
                      field.onChange(false);
                    }}
                  />
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-1 text-sm text-cool-gray-800">
                      <span>{t("agree-to")}</span>
                      <button
                        type="button"
                        className="font-medium text-cyan-600 hover:underline"
                        onClick={(event) => {
                          event.preventDefault();
                          setIsTermsOpen(true);
                        }}
                      >
                        {t("terms-of-service")}
                      </button>
                      <span>{t("agree-end")}</span>
                      <span className="text-red-500">*</span>
                    </div>
                    <p className="text-xs text-cool-gray-600">
                      {t("agree-to-terms-description")}
                    </p>
                  </div>
                </div>
              </label>
            )}
          />
          {errors.agree_to_terms?.message && (
            <p className="text-sm text-red-500">
              {errors.agree_to_terms?.message}
            </p>
          )}
        </div>
        <div className="cf-turnstile-container col-span-full">
          <div ref={turnstileRef} className="cf-turnstile" />
        </div>
        {turnstileError && (
          <p className="col-span-full text-sm text-red-600">
            {t("turnstile-verification-failed")}
          </p>
        )}
      </div>
      <TermsOfServiceDialog
        open={isTermsOpen}
        onOpenChange={setIsTermsOpen}
        onAccept={() => {
          setValue("agree_to_terms", true, {
            shouldValidate: true,
            shouldDirty: true,
          });
          setIsTermsOpen(false);
        }}
      />
    </div>
  );
}

export default RegisterCompanyForm;
